import AOS from 'aos';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../helpers/breakpoints';

const selectors = {
  carousel: '[data-splide-carousel]',
  progressBar: '[data-slider-progress] .splide__progress__bar',
};

export default class DynamicContentSlider {
  constructor(section) {
    this.el = section.el;
    this.data = section.data || {};
    this.section = section;
    this.carouselEl = this.el.querySelector(selectors.carousel);
    this.progressBar = this.el.querySelector(selectors.progressBar);
    this.splide = null;

    this._init();
  }

  _init() {
    const { mobileLayout, desktopLayout, slideCount } = this.data;

    if (!this.carouselEl || !slideCount) {
      return;
    }

    if (mobileLayout !== 'carousel' && desktopLayout !== 'carousel') {
      return;
    }

    this.splide = new Splide(this.carouselEl, this._buildOptions());

    if (this.progressBar) {
      this.splide.on('mounted move', () => this._updateProgress());
    }

    this.splide.on('mounted', () => AOS.refresh());

    this.splide.mount();
  }

  /**
   * Splide's `destroy` option is only honoured inside `breakpoints`, so the
   * media query direction is chosen so the grid breakpoint is never the base:
   * `min` when mobile is the carousel, `max` when desktop is.
   */
  _buildOptions() {
    const {
      mobileLayout,
      desktopLayout,
      perPageMobile,
      perPageTablet,
      perPageDesktop,
      peek,
      gap,
      gapDesktop,
      arrows,
      pagination,
      autoplay,
      interval,
      slideCount,
    } = this.data;

    const common = {
      perMove: 1,
      arrows,
      pagination,
      autoplay,
      interval,
      pauseOnHover: true,
      pauseOnFocus: true,
    };

    if (autoplay && slideCount > Math.max(perPageDesktop, perPageMobile)) {
      common.type = 'loop';
    }

    if (mobileLayout === 'carousel') {
      return {
        ...common,
        mediaQuery: 'min',
        perPage: perPageMobile,
        gap,
        padding: { left: 0, right: peek },
        breakpoints: {
          [unitlessBreakpoints.md]:
            desktopLayout === 'carousel'
              ? { perPage: perPageTablet, gap: gapDesktop, padding: 0 }
              : { destroy: true },
          [unitlessBreakpoints.lg]:
            desktopLayout === 'carousel'
              ? { perPage: perPageDesktop, gap: gapDesktop, padding: 0 }
              : { destroy: true },
        },
      };
    }

    // Mobile renders as a grid, desktop as a carousel.
    return {
      ...common,
      mediaQuery: 'max',
      perPage: perPageDesktop,
      gap: gapDesktop,
      padding: 0,
      breakpoints: {
        [unitlessBreakpoints.lg - 1]: { perPage: perPageTablet },
        [unitlessBreakpoints.md - 1]: { destroy: true },
      },
    };
  }

  _updateProgress() {
    const end = this.splide.Components.Controller.getEnd() + 1;
    const rate = Math.min((this.splide.index + 1) / end, 1);

    this.progressBar.style.width = `${100 * rate}%`;
  }

  onSectionUnload() {
    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }
  }
}
